import type { Html, Root, Text } from "mdast";
import type { Plugin } from "unified";

import { useCallback, useEffect, useRef } from "react";
import { visit } from "unist-util-visit";

import { TEX_REGEX } from "@/lib/has-math";
import { mathJaxProcessor } from "@/lib/mathjax-processor";

interface UseMathJaxRemarkReturn {
  /** A function to preprocess the content before sending it to Markdown */
  preprocessContent: (content: string) => string;
  /** The ref to the container element */
  ref: React.RefObject<HTMLDivElement | null>;
  /** The remark plugin for MathJax processing */
  remarkMathjax: Plugin<[], Root>;
}

export function useMathJaxRemark(content: string): UseMathJaxRemarkReturn {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<string>("");
  const latestContentRef = useRef<string>(content);
  const processingRef = useRef<null | Promise<boolean>>(null);

  /** Process MathJax elements using the queue system */
  const processMathJaxElements = useCallback(
    async (container: HTMLDivElement, currentContent: string) => {
      // Cancel any pending processing for old content
      if (processingRef.current) {
        processingRef.current = null;
      }

      // Start new processing
      const processingPromise = mathJaxProcessor.processContainer(
        container,
        currentContent
      );
      processingRef.current = processingPromise;

      try {
        await processingPromise;

        // Only update if this is still the current processing task
        if (processingRef.current === processingPromise) {
          contentRef.current = currentContent;
        }
      } catch (error) {
        console.warn("MathJax processing failed:", error);
      } finally {
        // Clear the processing reference if this was the current task
        if (processingRef.current === processingPromise) {
          processingRef.current = null;
        }
      }
    },
    []
  );

  // Split text around TeX expressions and create nodes
  const splitTextAroundTex = useCallback(
    (text: string): Array<Text | { type: "html"; value: string }> => {
      const matches = text.match(TEX_REGEX);
      if (!matches) {
        return [{ type: "text", value: text }];
      }

      const nodes: Array<Html | Text> = [];
      let lastIndex = 0;

      matches.forEach((match) => {
        const matchIndex = text.indexOf(match, lastIndex);

        // Add text before the match
        if (matchIndex > lastIndex) {
          nodes.push({
            type: "text",
            value: text.slice(lastIndex, matchIndex),
          });
        }

        // Add the TeX HTML node with a span tag
        nodes.push({
          type: "html",
          value: `<span data-tex="${match.replace(
            /"/g,
            "&quot;"
          )}">${match}</span>`,
        });

        lastIndex = matchIndex + match.length;
      });

      // Add remaining text after the last match
      if (lastIndex < text.length) {
        nodes.push({
          type: "text",
          value: text.slice(lastIndex),
        });
      }

      return nodes;
    },
    []
  );

  /** Remark plugin for MathJax processing */
  const remarkMathjax: Plugin<[], Root> = useCallback(() => {
    return (tree) => {
      visit(tree, "text", (node, index, parent) => {
        if (
          TEX_REGEX.test(node.value) &&
          typeof index === "number" &&
          parent?.children
        ) {
          const splitNodes = splitTextAroundTex(node.value);

          // Replace the current node with the split nodes
          parent.children.splice(index, 1, ...splitNodes);
        }
      });
    };
  }, [splitTextAroundTex]);

  useEffect(() => {
    latestContentRef.current = content;
  }, [content]);

  useEffect(() => {
    // Only process if content has actually changed and container is available
    if (!ref.current || contentRef.current === content) {
      return;
    }

    // Use requestAnimationFrame to ensure DOM has been updated
    const frame = requestAnimationFrame(() => {
      if (ref.current) {
        processMathJaxElements(ref.current, content);
      }
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [content, processMathJaxElements]);

  // Observe DOM changes to re-run MathJax when React re-renders identical content
  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (!ref.current) return;
        processMathJaxElements(ref.current, latestContentRef.current);
      });
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;
        // If any new span[data-tex] appears or existing ones change, schedule processing
        if (
          (mutation.addedNodes && mutation.addedNodes.length > 0) ||
          (mutation.removedNodes && mutation.removedNodes.length > 0)
        ) {
          schedule();
          break;
        }
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [processMathJaxElements]);

  /** Ensures that TeX expressions are properly escaped for MathJax before sending to Markdown */
  const preprocessContent = useCallback(
    (content: string) =>
      content
        .replace(/\\\[/g, "\\\\[")
        .replace(/\\\]/g, "\\\\]")
        .replace(/\\\(/g, "\\\\(")
        .replace(/\\\)/g, "\\\\)"),
    []
  );

  return { preprocessContent, ref, remarkMathjax };
}
